class ProductSelector extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      form: 'form',
      fields: 'input[type=radio], select',
      atcBar: 'atc-bar',
      swatch: '.swatch',
      submitButton: '[name="add"]',
      submitButtonSticky: '.js-submit-sticky',
      cartDrawer: 'cart-drawer',
      pdpPrice: '.js-product .js-price',
      atcBarPrice: 'atc-bar .atc-bar__price',
      buttonsContainer: '.js-variant-buttons',
      colorText: '.js-color-text',
      shippingTextWrapper: '.js-shipping-info',
      shippingPreorderDateContainer: ".js-preorder-date-container",
      preorderProperty: 'data-preorder-property',
      deliveryDateProperty: 'data-delivery-date-property',
    };
  }

  connectedCallback() {
    this.atcBar = document.querySelector(this.selectors.atcBar);
    this.fields = this.querySelectorAll(this.selectors.fields);
    this.swatches = this.querySelectorAll(this.selectors.swatch);
    this.form = this.querySelector(this.selectors.form);
    this.submitButton = this.form.querySelector(
      this.selectors.submitButton
    );
    this.cartDrawer = document.querySelector(
      this.selectors.cartDrawer
    );
    this.variants = JSON.parse(
      this.querySelector('[type="application/json"]').textContent
    );

    this.#updateOptions();
    this.#updateVariant();
    this.#attachEvents();
    this.#setInitialImage();
    if (this.dataset.hasSelectedVariant === "true") {
      this.#changeButtonText(window.variantStrings.addToCart, true);
    }
    this.#updateMedia();
    this.#updateShippingText();
    this.#toggleProperties();
    this.#updateColorName();
  }

  #attachEvents() {
    this.form.addEventListener(
      'submit',
      this.#handleSubmit.bind(this)
    );

    for (const swatch of this.swatches) {
      swatch.addEventListener('click', (event) => {
        this.#changeImage(event);
      });
    }

    for (const field of this.fields) {
      field.addEventListener('change', (event) => {
        this.#handleVariantChange(event);
      });
    }
  }

  #handleOptionChange() {
    this.options.forEach(option => {
      const optionElement = this.querySelector(`[value="${option}"]`);
      if(!optionElement) return;

      const wrapper = optionElement.closest('.js-variant-buttons');
      if(!wrapper) return;

      const textElement = wrapper.querySelector('.js-option-text');
      if(!textElement) return;

      textElement.textContent = option;
    });
  }

  /**
   * Handle currentVariant change.
   *
   * @param {Event} event
   * @returns {Void}
   */
  #handleVariantChange(event) {
    if (event.target.type === 'number') return;
    this.#updateOptions();
    this.#updateVariant();
    this.#updateVariantInput();
    this.#handleOptionChange();

    if (!this.currentVariant || !this.currentVariant?.available) {
      this.#disableSubmitButton();
    } else {
      this.#enableSubmitButton();
    }

    const { price, compare_at_price } = this.currentVariant;
    this.#changePrice(price, compare_at_price);
    this.#changeButtonText(window.variantStrings.addToCart, true);
    this.#updateMedia();
    this.#updateURL();
    this.#clearColorNames();
    this.#updateColorName();
    this.#updateShippingText();
    this.#toggleProperties();

    document.dispatchEvent(
      new CustomEvent('product-selector:change-variant', {
        bubbles: true,
        detail: {
          name: event.target.getAttribute('name'),
          value: event.target.value,
          available: this.currentVariant.available,
          newVariant: this.currentVariant
        },
      })
    );
  }

  async #handleSubmit(event) {
    event.preventDefault();
    this.#disableSubmitButton();

    const formData = new FormData(this.form);
      formData.append(
        'sections',
        this.cartDrawer
          .getSectionsToRender()
          .map((section) => section.section)
      );
      formData.append('sections_url', window.location.pathname);

      const responseData = await addToCart(formData);
      responseData && this.cartDrawer.renderContents(responseData);

      this.#enableSubmitButton();
  }

  #updateVariantInput() {
    const input = this.querySelector('[name="id"]');
    input.value = this.currentVariant?.id;
  }

  #updateMedia() {
    if (!this.currentVariant || !this.currentVariant.featured_media)
      return;

    const productMedia = document.querySelector('product-media');
    productMedia?.setActiveSlide(
      this.currentVariant.featured_media.id
    );

    if (!this.querySelector('.js-product-variants')) {
      productMedia?.filterByVariant(
        this.currentVariant.featured_media.position
      );
    }
  }

  #disableSubmitButton() {
    this.querySelector(this.selectors.submitButton)?.setAttribute(
      'disabled',
      true
    );
    this.querySelector(this.selectors.submitButton)?.classList.add(
      'disabled'
    );
  }

  #enableSubmitButton() {
    this.querySelector(
      this.selectors.submitButton
    )?.removeAttribute('disabled');
    this.querySelector(
      this.selectors.submitButton
    )?.classList.remove('disabled');

    const small = this.querySelector(`${this.selectors.submitButton} small`)
    if(!small) return;
    
    small.textContent = this.currentVariant.compareAtPrice ?? this.currentVariant.price;
  }

  /**
   * Change submit button text.
   *
   * @param {String} text
   * @param {Boolean} hasPrice
   * @returns {Void}
   */
  #changeButtonText(text, hasPrice) {
    const buttons = document.querySelectorAll(`${this.selectors.submitButton}, ${this.selectors.submitButtonSticky}`);

    buttons.forEach((button) =>{
      if (!button) return;
  
      const currentVariant = typeof this.currentVariant === 'object'
        ? this.currentVariant
        : this.variants[0];
  
      const { inventory_policy, inventory_quantity } = this.currentVariant;
      let buttonLabel = text;
  
      if (inventory_policy == 'continue' && inventory_quantity <= 0) {
        buttonLabel = window.variantStrings.preOrderNow;
      }
  
      if (hasPrice) {
        const { price } = currentVariant;
        const formattedPrice = Shopify.formatMoney(
          price,
          Shopify.current_money_format
        );
        button.innerHTML = `${buttonLabel} <small>${formattedPrice}</small>`;
      } else {
        button.innerHTML = `${buttonLabel}`;
      }
    })
  }

  #updateShippingText() {
    const shippingTextWrapper = document.querySelector(this.selectors.shippingTextWrapper);

    if (!shippingTextWrapper) return;

    const { available, inventory_policy, inventory_quantity, next_available_date } = this.currentVariant;
    const shippingTextPreorderDateContainer = shippingTextWrapper.querySelector(this.selectors.shippingPreorderDateContainer);

    shippingTextWrapper.classList.toggle('product__shipping-wrapper--preorder', inventory_policy == 'continue' && inventory_quantity <= 0);

    if (shippingTextPreorderDateContainer && inventory_policy == 'continue' && inventory_quantity <= 0) {
      shippingTextPreorderDateContainer.textContent = window.variantStrings.preOrderMessage.replace('[date]', next_available_date);
    }

    shippingTextWrapper.classList.toggle('hidden', !available);
  }

  #setInitialImage() {
    let color = this.querySelector(
      '.product-selector__buttons input:checked'
    )?.value;
    if (!color) {
      color = this.querySelector(
        '.product-selector__buttons input:first-child'
      )?.value;
    }
    color &&
      this.closest('.product')
        .querySelector('product-media')
        ?.filterByColor(color);

  }

  #changeImage(event) {
    const variantColor = event.target.getAttribute('title');

    this.closest('.product')
      .querySelector('product-media')
      ?.filterByColor(variantColor);
  }

  /**
   * Update options based on selected fields.
   */
  #updateOptions() {
    this.options = Array.from(
      this.querySelectorAll('input[type="radio"]:checked, select'),
      (el) => el.value
    );
  }

  /**
   * Update currentVariant property.
   */
  #updateVariant() {
    this.currentVariant = this.variants.find((variant) => {
      return !variant.options
        .map((option, index) => this.options[index] === option)
        .includes(false);
    });

    if (this.options.length === 0) {
      this.currentVariant = this.variants[0]
    } else {
      if (!this.currentVariant) {
        this.currentVariant = this.querySelector('[name="id"]').value;
      }
    }

    this.#setInitialImage();
  }

  #formatMoney(amount) {
    return Shopify.formatMoney(amount, Shopify.current_money_format);
  }

  #createNode(tag, className, text) {
    const el = document.createElement(tag);
    if (className) el.className = className;
    if (text) el.textContent = text;

    return el;
  }

  #changePrice(price, compareAtPrice) {
    const mainPrice = document.querySelector('.js-product .js-price');

    if (mainPrice) {
      mainPrice.replaceChildren();

      if (compareAtPrice && compareAtPrice > price) {
        const discount = compareAtPrice - price;
        const percent = Math.round((discount / compareAtPrice) * 100);
        const discountText = window.variantStrings?.discountText?.replace('[amount]', this.#formatMoney(discount)) || '';
    
        mainPrice.appendChild(this.#createNode('del', null, this.#formatMoney(compareAtPrice)));
        mainPrice.appendChild(this.#createNode('ins', null, this.#formatMoney(price)));
        mainPrice.appendChild(this.#createNode('span', 'product__price-descount-percent', `-${percent}%`));
        mainPrice.appendChild(this.#createNode('span', 'product__price-descount-text', discountText));
      } else {
        mainPrice.appendChild(this.#createNode('span', null, this.#formatMoney(price)));
      }
    }

    document.dispatchEvent(
      new CustomEvent('product-selector:change-price', {
        bubbles: true,
        detail: {
          finalPrice: price,
          comparePrice: compareAtPrice,
        },
      })
    );
  }

  #updateURL() {
    if (
      !this.currentVariant ||
      !window.location.pathname.includes('products')
    ) {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    params[params.has('variant') ? 'set' : 'append'](
      'variant',
      this.currentVariant.id
    );
    window.history.replaceState(
      {},
      '',
      `${this.dataset.url}?${params.toString()}`
    );
  }

  #clearColorNames() {
    this.querySelectorAll(this.selectors.colorText).forEach((text) => text.innerHTML = '');
  }

  #updateColorName() {
    const swatch = this.querySelector('input[type="radio"]:checked');
    const color = swatch?.closest(this.selectors.buttonsContainer).querySelector(this.selectors.colorText);
    color && (color.innerHTML = this.options[0]);
  }

  #toggleProperties() {
    const { inventory_policy, inventory_quantity, next_available_date } = this.currentVariant;

    if (inventory_policy == 'continue' && inventory_quantity <= 0) {
      if (this.querySelector('input[id="fabrikator-product-property"]')) {
        const inputNote = document.createElement('input');

        inputNote.type = 'hidden';
        inputNote.name = `properties[Note]`;
        inputNote.value = window.cartStrings.preOrderDeliveryMessage.replace('[date]', next_available_date);
        inputNote.setAttribute(this.selectors.preorderProperty, true);

        this.form.appendChild(inputNote);
      }

      const inputDeliveryDate = document.createElement('input');

      inputDeliveryDate.type = 'hidden';
      inputDeliveryDate.name = `properties[_delivery_date]`;
      inputDeliveryDate.value = next_available_date;
      inputDeliveryDate.setAttribute(this.selectors.deliveryDateProperty, true);

      this.form.appendChild(inputDeliveryDate);
    } else {
      if (this.form.querySelector(`[${this.selectors.preorderProperty}]`)) {
        this.form.querySelector(`[${this.selectors.preorderProperty}]`).remove();
      }

      if (this.querySelector(`[${this.selectors.deliveryDateProperty}]`)) {
        this.querySelector(`[${this.selectors.deliveryDateProperty}]`).remove();
      }
    }
  }
}

customElements.define('product-selector', ProductSelector);
